import moment from 'moment';
import { useMemo, useCallback, useRef, useContext, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { isUndefined, omit, omitBy } from 'lodash';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { createCachedSelector } from 're-reselect';
import { SLOT_WARNING_TYPES } from 'scheduling/constants';
import useOptimisticMutation from '../useOptimisticMutation';
import { API_DATE_FORMAT, REQUEST_STATUS } from '../../constants';
import * as api from '../../services/api';
import { mappedArray } from '../../utils/array';
import useToaster from '../useToaster';
import {
  getSpecificProjectStoreKey,
  handleMutatedBookingData,
  handleMutatedUnBookingData
} from '../../utils/scheduling';
import useAccount from './useAccount';

const REFETCH_INTERVAL_MS = 1000 * 30;

const getDate = (date) => moment(date).format(API_DATE_FORMAT);

const selector = createCachedSelector(
  (data) => data.data,
  (data) => {
    const project = (() => {
      if (!data?.project) return null;
      return {
        ...data.project,
        shifts: data.project.shifts.map((shift) => ({
          ...shift,
          unbookings: (data?.unbookings || []).filter(({ shiftId }) => shiftId === shift.id)
        }))
      };
    })();

    const shiftsByEmployee = (() => {
      const mappedShifts = {};
      if (!data?.project) {
        return {};
      }
      const { id, labelId, name, shifts } = data?.project || {};
      shifts.forEach(({ id: shiftId, slots, startTime, positionId, needed, isOpenShift }) => {
        (slots || []).forEach(({ employeeId, ...slot }) => {
          if (!employeeId) {
            return;
          }
          const formattedDate = getDate(startTime);
          const key = `${formattedDate}-${employeeId}`;

          mappedShifts[key] = [
            ...(mappedShifts[key] || []),
            { projectId: id, labelId, name, positionId, shiftId, needed, slotId: slot.id, isOpenShift, ...slot }
          ];
        });
      });

      return mappedShifts;
    })();

    const unbookings = data?.unbookings;

    const employees = Object.values(data?.mappedEmployees || {}).filter(({ isInternal }) => !!isInternal);

    const employeesMap = mappedArray(employees || []);

    return {
      data,
      project,
      shiftsByEmployee,
      employeesMap,
      unbookings,
      employees,
      allEmployeesMap: data?.mappedEmployees || {}
    };
  }
)({
  keySelector: (data, storeKey) => `${storeKey.join('#')}`
});

const useProject = ({
  projectId,
  branchId,
  includes = [],
  enable = true,
  refetchInterval = true,
  getFromStoreKey = true,
  isLite = false,
  withPayroll = false,
  context,
  preferCache = false
}) => {
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const hasError = useRef(false);
  const { addError } = useToaster();
  const { t } = useContext(ConfigContext);

  const storeKey = useMemo(() => getSpecificProjectStoreKey({ projectId: Number(projectId), isLite, withPayroll }), [
    isLite,
    projectId,
    withPayroll
  ]);

  const handleChangeShiftMutation = useCallback(
    ({ slotId, srcShift, dstShift }) => {
      const previousProjectData = queryClient.getQueryData(storeKey);

      // PERFORMANCE - CAN BE COMBINED

      //UN-BOOK MUTATION
      const mutatedUnBookingData = handleMutatedUnBookingData({
        previousData: previousProjectData,
        slotId,
        shift: srcShift
      });

      // BOOK MUTATION
      const mutatedBookingData = handleMutatedBookingData({
        previousData: { ...previousProjectData, data: mutatedUnBookingData },
        data: [dstShift],
        projectId
      });

      queryClient.setQueryData(storeKey, { ...previousProjectData, data: mutatedBookingData });
    },
    [projectId, queryClient, storeKey]
  );

  const handleExternalChangeShiftMutation = useCallback(
    ({ shiftInClientSide, shiftInSupplierSide }) => {
      const previousData = queryClient.getQueryData(storeKey);

      const { srcShift, dstShift } = shiftInClientSide;
      const {
        dstSupplierShiftId,
        orderId,
        slotId,
        srcSupplierShiftId,
        supplierAccountId,
        suppliersShiftIdsConnectedToSrcClientShift = [],
        suppliersShiftIdsConnectedToDestClientShift = []
      } = shiftInSupplierSide;

      const matchingOrderShifts =
        (previousData?.data?.project?.orders || []).find(({ id }) => id === orderId)?.shifts || [];

      const matchingOrderShift = matchingOrderShifts.find(({ id }) => id === srcSupplierShiftId);
      const matchingSlotData = (matchingOrderShift.slots || []).find(({ id }) => id === slotId);

      const newData = {
        ...previousData.data,

        // EMPLOYEES - not needed since already exists

        // PROJECT
        project: {
          ...previousData.data.project,
          shifts: previousData.data.project.shifts?.map((shift) => {
            if (shift.id === srcShift.id) {
              return {
                ...shift,
                stats: srcShift.stats,
                orderedToShiftStats: {
                  ...shift.orderedToShiftStats,
                  stats: {
                    ...(srcShift.orderedToShiftStats.stats?.[supplierAccountId]
                      ? {
                          ...shift.orderedToShiftStats.stats,
                          [supplierAccountId]: srcShift.orderedToShiftStats.stats[supplierAccountId]
                        }
                      : {
                          ...omit(shift.orderedToShiftStats.stats, [supplierAccountId])
                        })
                  }
                }
              };
            }

            if (shift.id === dstShift.id) {
              return {
                ...shift,
                stats: dstShift.stats,
                orderedToShiftStats: {
                  ...shift.orderedToShiftStats,
                  stats: {
                    ...shift.orderedToShiftStats.stats,
                    [supplierAccountId]: dstShift.orderedToShiftStats.stats[supplierAccountId]
                  }
                }
              };
            }
            return shift;
          }),
          orders: previousData.data.project.orders?.map((order) => {
            if (order.id !== orderId) {
              return order;
            }

            return {
              ...order,
              shifts: (order.shifts || []).map((shift) => {
                if (suppliersShiftIdsConnectedToSrcClientShift.includes(shift.id)) {
                  return {
                    ...shift,
                    slots:
                      shift.id === srcSupplierShiftId ? shift.slots.filter(({ id }) => id !== slotId) : shift.slots,

                    // remove (1) from the ordered shift
                    clientShift: shift.clientShift
                      ? { ...shift.clientShift, needed: Math.max(shift.clientShift.needed - 1, 0) }
                      : undefined,
                    needed: Math.max(shift.needed - 1, 0)
                  };
                }

                if (suppliersShiftIdsConnectedToDestClientShift.includes(shift.id)) {
                  return {
                    ...shift,
                    slots:
                      shift.id === dstSupplierShiftId
                        ? shift.slots.concat({ ...matchingSlotData, shiftId: dstSupplierShiftId })
                        : shift.slots,

                    // add (1) from the ordered shift
                    clientShift: shift.clientShift
                      ? { ...shift.clientShift, needed: shift.clientShift.needed + 1 }
                      : undefined,
                    needed: shift.needed + 1
                  };
                }

                return shift;
              })
            };
          })
        }
      };

      queryClient.setQueryData(storeKey, { ...previousData, data: newData });
    },
    [queryClient, storeKey]
  );

  const refetchIntervalValue = useRef(REFETCH_INTERVAL_MS);

  const { isLoading, data: selectorData, refetch, error, isFetchedAfterMount, dataUpdatedAt, isFetching } = useQuery(
    storeKey,
    () => {
      return api.fetchProject({
        accountId,
        branchId,
        projectId,
        includes,
        isLite,
        withPayroll,
        context
      });
    },
    {
      enabled: !!enable && !hasError.current && !!(branchId && projectId),
      staleTime: preferCache ? Infinity : 0,
      select: (data) => selector(data, storeKey),
      refetchInterval: preferCache ? false : refetchInterval ? refetchIntervalValue.current : null,
      retry: (_, err) => {
        if (err.message.includes('400')) {
          hasError.current = true;
          addError(t('projectNotFound'));
          return false;
        }
        return true;
      }
    }
  );

  const { project, shiftsByEmployee = {}, employeesMap = {}, allEmployeesMap = {}, unbookings = [], employees } =
    selectorData || {};

  useEffect(() => {
    // stop refetching when over 50 employees
    if (projectId === 1375303 || projectId === 1381646) {
      refetchIntervalValue.current = true;
    } else if (projectId !== 1208700 && (employees || []).length > 50) {
      refetchIntervalValue.current = false;
    }
  }, [employees, projectId]);

  const { mutateAsync: changeShiftExternalEmployee } = useOptimisticMutation(
    storeKey,
    ({ srcClientShiftId, dstClientShiftId, originSupplierEmployeeSlotId, directSupplierProjectId }) =>
      api.changeShiftExternalEmployee({
        accountId,
        branchId,
        projectId,
        srcClientShiftId,
        dstClientShiftId,
        originSupplierEmployeeSlotId,
        directSupplierProjectId
      }),
    null,
    {
      refetchOnSuccess: false,
      // Booked's handleChangeShiftExternalEmployee owns the error toast — don't double-toast here
      showErrorToaster: false,
      onSuccess: (_, { data: { shiftInClientSide = {}, shiftInSupplierSide = {}, newShiftWasCreated } }) => {
        if (newShiftWasCreated) {
          queryClient.invalidateQueries(storeKey, { exact: true });

          return;
        }
        handleExternalChangeShiftMutation({ shiftInClientSide, shiftInSupplierSide });
        queryClient.invalidateQueries(['OSProjectInfo', accountId, branchId, projectId]);
      }
    }
  );

  const { mutateAsync: changeShiftInternalEmployee } = useOptimisticMutation(
    storeKey,
    ({ shiftId, slotId, destShiftId, withValidations = true }) => {
      return api.changeShiftInternalEmployee({
        accountId,
        branchId,
        projectId,
        shiftId,
        slotId,
        destShiftId,
        withValidations
      });
    },

    // eslint-disable-next-line unused-imports/no-unused-vars
    ({ previousData, destShiftId, shiftId: srcShiftId, slotId }) => {
      // TODO: not sure needed, depends how fast this will run
      // const srcShiftData = previousData.data.project.shifts.find(({ id }) => id === srcShiftId);
      // const srcSlotData = (srcShiftData?.slots || []).find(({ id }) => id === slotId);
      // const updatedMovedSlot = {
      //   ...(srcSlotData || {}),
      //   shiftId: destShiftId
      // };
      // const newItem = {
      //   ...previousData.data,
      //   project: {
      //     ...previousData.data.project,
      //     shifts: previousData.data.project.shifts
      //       .map((shift) => {
      //         if (![destShiftId, srcShiftId].includes(shift.id)) {
      //           return shift;
      //         }
      //         if (shift.id === srcShiftId) {
      //           return {
      //             ...shift,
      //             //remove from src shift current slot
      //             slots: (shift.slots || []).filter((slot) => slot.id !== slotId)
      //           };
      //         }
      //         if (shift.id === destShiftId) {
      //           return {
      //             ...shift,
      //             //add new slot
      //             slots: [...(shift.slots || []), updatedMovedSlot]
      //           };
      //         }
      //         return null;
      //       })
      //       .filter((curr) => !!curr)
      //   }
      // };
      // return { ...previousData, data: newItem };
    },
    {
      showErrorToaster: false,
      refetchOnSuccess: false,
      onSuccess: ({ slotId }, { data: { dstShift, srcShift } }) => {
        handleChangeShiftMutation({ dstShift, srcShift, slotId });
        queryClient.invalidateQueries(['projectInfo', accountId, branchId, projectId]);
      }
    }
  );

  const { mutateAsync: swapShift } = useOptimisticMutation(
    storeKey,
    ({ src, dst, withValidations }) => api.swapShift({ accountId, branchId, projectId, src, dst, withValidations }),
    null,
    {
      refetchOnSuccess: false,
      // SwapShiftModal owns swap errors (violations modal / fallback toast) — don't double-toast here
      showErrorToaster: false,
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey, { exact: true });
        queryClient.invalidateQueries(['projectInfo', accountId, branchId, projectId]);
        queryClient.invalidateQueries(['OSProjectInfo', accountId, branchId, projectId]);
      }
    }
  );

  const { mutateAsync: updateShift } = useOptimisticMutation(
    storeKey,
    ({ shiftId, isOverbooking }) =>
      api.updateShift({
        accountId,
        branchId,
        projectId,
        shiftId,
        isOverbooking
      }),
    ({ previousData, shiftId, isOverbooking }) => {
      const newItem = {
        ...previousData.data,
        project: {
          ...previousData.data.project,
          shifts: previousData.data.project.shifts.map((shift) =>
            shift.id !== shiftId ? shift : { ...shift, ...omitBy({ isOverbooking }, isUndefined) }
          )
        }
      };
      return { ...previousData, data: newItem };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['projects', accountId]);
      }
    }
  );

  const { mutateAsync: addShiftWithEmployee } = useMutation(
    (values) =>
      api.addShift({
        accountId,
        branchId,
        projectId,
        values
      }),
    {
      onSuccess: ({ data }) => {
        const previousData = queryClient.getQueryData(storeKey);
        const newItem = {
          ...previousData.data,
          project: {
            ...previousData.data.project,
            shifts: [...previousData.data.project.shifts, data]
          }
        };

        queryClient.setQueryData(storeKey, { ...previousData, data: newItem });
      }
    }
  );

  const { mutateAsync: refreshProjectStats } = useMutation(
    () =>
      api.refreshProjectStats({
        accountId,
        branchId,
        projectId
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey, { exact: true });
      }
    }
  );

  const { mutateAsync: updateBulkShiftHours } = useMutation(
    ({ shiftIds, minutes, offsetTarget, startTime, endTime, createOrderChangeBulk, notifyEmployeesUponChange }) =>
      api.updateProjectShiftsHours({
        accountId,
        branchId,
        projectId,
        shiftIds,
        minutes,
        offsetTarget,
        startTime,
        endTime,
        createOrderChangeBulk,
        notifyEmployeesUponChange
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['projects', accountId]);
      }
    }
  );

  const { mutateAsync: updateProject } = useMutation(
    (params) =>
      api.updateProject({
        accountId,
        branchId,
        projectId,
        project: params
      }),
    {
      onMutate: (params) => {
        const previousData = queryClient.getQueryData(storeKey);

        if (!previousData) {
          return;
        }
        const newItem = {
          ...previousData.data,
          project: {
            ...previousData.data.project,
            ...params
          }
        };

        queryClient.setQueryData(storeKey, {
          ...previousData,
          data: newItem
        });
      }
    }
  );

  const refetchProject = useCallback(() => {
    refetch?.();
  }, [refetch]);

  // Optimistic mutation function to update project store data for release request approval
  const handleOptimisticReleaseRequestUpdate = useCallback(
    (releaseRequest) => {
      try {
        // Get current project data from the store
        const previousData = queryClient.getQueryData(storeKey);

        if (!previousData?.data?.project) {
          // eslint-disable-next-line no-console
          console.warn('No project data found in store for optimistic update');
          return;
        }

        if (!releaseRequest.status || !releaseRequest.shiftId || !releaseRequest.slotId) {
          return;
        }

        const newItem = {
          ...previousData.data,
          project: {
            ...previousData.data.project,
            shifts: (previousData.data.project.shifts || []).map((shift) => {
              if (shift.id !== releaseRequest.shiftId) {
                return shift;
              }

              let updatedSlots = shift.slots;

              // if declined, filter out the request warning of this specific shift
              if (releaseRequest.status === REQUEST_STATUS.DECLINED) {
                updatedSlots = (shift.slots || []).map((slot) => {
                  if (slot.id !== releaseRequest.slotId) {
                    return slot;
                  }

                  return {
                    ...slot,
                    warnings: slot.warnings.filter(
                      ({ type, metadata = {} }) =>
                        type !== SLOT_WARNING_TYPES.RELEASE_REQUEST.type || metadata.shiftId !== releaseRequest.shiftId
                    )
                  };
                });
              }

              // remove employee (unbook)
              if (releaseRequest.status === REQUEST_STATUS.APPROVED) {
                updatedSlots = (shift.slots || []).filter(({ id }) => id !== releaseRequest.slotId);
                // stats issue + unbooking issue
              }

              return {
                ...shift,
                slots: updatedSlots
              };
            })
          }
        };

        queryClient.setQueryData(storeKey, {
          ...previousData,
          data: newItem
        });
      } catch (optimisticUpdateError) {
        // eslint-disable-next-line no-console
        console.error('Failed to perform optimistic release request update:', optimisticUpdateError);
      }
    },
    [storeKey, queryClient]
  );

  if (!enable && !getFromStoreKey) {
    return {};
  }

  return {
    employees,
    storeKey,
    isLoading,
    project,
    employeesMap,
    allEmployeesMap,
    unbookings,
    shiftsByEmployee,
    changeShiftExternalEmployee,
    changeShiftInternalEmployee,
    swapShift,
    refetch: refetchProject,
    dataUpdatedAt,
    addShiftWithEmployee,
    updateProject,
    updateBulkShiftHours,
    updateShift,
    refreshProjectStats,
    handleOptimisticReleaseRequestUpdate,
    isFetchedAfterMount,
    isFetching,
    error
  };
};

export default useProject;
